/** Formatting shared by every screen. */

export const c2f = (c: number) => (c * 9) / 5 + 32

/** Temperatures are Celsius on the wire and Fahrenheit on screen. */
export function temp(c: number | null | undefined, digits = 0): string {
  if (c === null || c === undefined) return '—'
  return `${c2f(c).toFixed(digits)}°F`
}

export function degC(c: number | null | undefined, digits = 1): string {
  if (c === null || c === undefined) return '—'
  return `${c.toFixed(digits)}°C`
}

export function pct(v: number | null | undefined, digits = 0): string {
  if (v === null || v === undefined) return '—'
  return `${(v * 100).toFixed(digits)}%`
}

/** "3m ago" — operators scan for staleness, not for wall-clock times. */
export function ago(iso: string | null | undefined): string {
  if (!iso) return 'never'
  const s = (Date.now() - new Date(iso).getTime()) / 1000
  if (s < 0) return 'just now'
  if (s < 60) return `${Math.floor(s)}s ago`
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

export function when(iso: string | null | undefined, tz?: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    timeZone: tz, month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

/**
 * How fresh a reading is.
 *
 * Fresh is unremarkable and stays neutral. Only "we have stopped hearing from
 * this home" earns a hue, because that is the row an operator has to act on —
 * colouring the healthy majority green teaches people to ignore colour.
 *
 * The thresholds are multiples of the *actual* sampling period, which the
 * service reports at /admin/meta. Five minutes old is perfectly healthy at a
 * five-minute cadence and alarming at a one-minute one, so a hardcoded number
 * turns every row red the first time the interval is tuned.
 */
export const DEFAULT_POLL_S = 300

export function staleness(iso: string | null | undefined, pollS = DEFAULT_POLL_S):
    { cls: '' | 'on' | 'danger'; label: string } {
  if (!iso) return { cls: 'danger', label: 'no data' }
  const s = (Date.now() - new Date(iso).getTime()) / 1000
  // One interval: the sample we expect to have. Three: two polls missed, which
  // is no longer a slow pass.
  if (s < pollS * 1.2) return { cls: 'on', label: ago(iso) }
  if (s < pollS * 3) return { cls: '', label: ago(iso) }
  return { cls: 'danger', label: ago(iso) }
}

export const ACTOR_LABEL: Record<string, string> = {
  occupant_device: 'Occupant (wall)',
  occupant_app: 'Occupant (app)',
  loop: 'Our loop',
  admin: 'Operator',
}

/**
 * Whether the blower is running. Nest never reports that directly: its Fan
 * trait is only the manual fan timer, which stays OFF while the thermostat runs
 * the blower itself for heating or cooling. So the fan is on when the equipment
 * is running or someone turned the timer on.
 */
export function fanOn(hvac: string | null | undefined, fan: string | null | undefined): boolean {
  return (!!hvac && hvac !== 'OFF') || fan === 'ON'
}
